/**
 * Ad spend entry rules for the browser. No imports, deliberately (see reportContract.ts): a module
 * that reaches `lib/firebase` cannot be loaded by the test runner.
 *
 * MIRRORS adSpend/schema.ts on the server. The server is the gate and refuses anything these rules
 * would refuse; they exist so the operator is told at the field, not after a round trip. Duplicated
 * rather than shared because the browser bundle cannot reach the functions package and the tracker
 * isolation fence forbids a shared one.
 */

export type GstBasis = 'inc' | 'ex';

export interface AdSpendEntry {
  /** `YYYY-MM`. */
  month: string;
  channel: string;
  /** Integer cents, as entered (inc or ex GST per `gst`). */
  amountCents: number;
  gst: GstBasis;
}

export const MAX_CHANNEL_LENGTH = 60;
export const MAX_AD_SPEND_CENTS = 10_000_000_000;

/**
 * The GST rule, stated once and shown on the page: inc GST divided by 1.1, rounded to the nearest
 * cent (half a cent up); ex GST as entered. Same integer arithmetic as the server, so the preview
 * beside the field equals the figure the report computes.
 */
export const exGstCents = (entry: Pick<AdSpendEntry, 'amountCents' | 'gst'>): number =>
  entry.gst === 'ex' ? entry.amountCents : Math.round((entry.amountCents * 10) / 11);

export const GST_RULE = 'Amounts entered including GST are divided by 1.1 and rounded to the nearest cent; amounts entered ex GST are used as entered. Totals and cost per lead are ex GST.';

/**
 * Dollars as typed ("1,710", "$1710.50") to integer cents, or null when it is not an amount.
 *
 * Parsed from the string rather than `Math.round(parseFloat(x) * 100)`: 1710.35 * 100 is
 * 171034.99999999997 in binary floating point, and a rounding step that happens to save it here
 * would not save every value.
 */
export function dollarsToCents(input: string): number | null {
  const text = input.replace(/[$,\s]/g, '');
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(text);
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return Number.isSafeInteger(cents) && cents <= MAX_AD_SPEND_CENTS ? cents : null;
}

export const channelMatchKey = (channel: string): string => channel.replace(/\s+/g, ' ').trim().toLowerCase();

/** Why this entry cannot be added to `existing`, or null when it can. */
export function entryProblem(entry: AdSpendEntry, existing: readonly AdSpendEntry[]): string | null {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(entry.month)) return 'Choose a month.';
  const channel = entry.channel.replace(/\s+/g, ' ').trim();
  if (channel === '') return 'Name the channel, for example Google.';
  if (channel.length > MAX_CHANNEL_LENGTH) return `Keep the channel to ${MAX_CHANNEL_LENGTH} characters.`;
  if (!Number.isInteger(entry.amountCents) || entry.amountCents < 0 || entry.amountCents > MAX_AD_SPEND_CENTS) {
    return 'Enter the amount in dollars and cents.';
  }
  const key = `${entry.month}|${channelMatchKey(channel)}`;
  if (existing.some(e => `${e.month}|${channelMatchKey(e.channel)}` === key)) {
    return `${channel} already has an entry for ${entry.month}. Edit or remove that one instead.`;
  }
  return null;
}
